"use client"

import { useState, useEffect } from "react"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Grid,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import axios from "axios"
import BaseURL from "../../api/AppURL";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "98%", sm: "95%", md: "85%", lg: "75%" },
  maxWidth: 1200,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  maxHeight: "95vh",
  overflowY: "auto",
}

export default function EditPurchaseModal({ open, onClose, purchaseId, onUpdateSuccess }) {
  const [loading, setLoading] = useState(false)
  const [allProducts, setAllProducts] = useState([])
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [supplier, setsupplier] = useState(null)
  const [formData, setFormData] = useState({
    purchaseDate: "",
    receiveDate: "",
    paymentMode: "",
    deliveryBy: "",
    mobile_no: "",
    address: "",
  })
  const [productRows, setProductRows] = useState([])
  const [totalSum, setTotalSum] = useState(0)
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMsg, setAlertMsg] = useState("")
  const [alertType, setAlertType] = useState("error")
  const [saving, setSaving] = useState(false)
  const [loadingProducts, setLoadingProducts] = useState(false) // for Autocomplete loading spinner
  const [removedRows, setRemovedRows] = useState([]) // track removed rows for API

  // Fetch all products for product dropdown (like AddpurchaseModal)
  const fetchAllProducts = async () => {
    setLoadingProducts(true)
    try {
      const token = localStorage.getItem("auth_token")
      const res = await axios.get(`${BaseURL}/all/rawpurchase`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      setAllProducts(res.data.rawMaterials || [])
    } catch (err) {
      setAllProducts([])
    } finally {
      setLoadingProducts(false)
    }
  }

  // Fetch purchase details on open
  useEffect(() => {
    if (open && purchaseId) {
      setRemovedRows([]) // reset on modal open
      fetchPurchaseDetails()
      fetchAllProducts() // fetch products for dropdown
    }
    // eslint-disable-next-line
  }, [open, purchaseId])

  // Calculate total sum
  useEffect(() => {
    setTotalSum(productRows.reduce((acc, row) => acc + Number(row.totalPrice), 0))
  }, [productRows])

  const showAlert = (msg, type = "error") => {
    setAlertMsg(msg)
    setAlertType(type)
    setAlertOpen(true)
  }

  const fetchPurchaseDetails = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem("auth_token")
      const res = await axios.get(`${BaseURL}/get/purchasedetails/${purchaseId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      setsupplier(res.data.supplier)
      setFormData({
        purchaseDate: res.data.purchase.purchase_date || "",
        receiveDate: res.data.purchase.receive_date || "",
        paymentMode: res.data.purchase.payment_mode || "",
        deliveryBy: res.data.purchase.receive_by || "",
        mobile_no: res.data.purchase.supplier?.mobile_no || "",
        address: res.data.purchase.supplier?.address || "",
      })
      // Map purchaseDetails to productRows
      const rows = (res.data.purchaseDetails || []).map(detail => {
        // Find product object (from api/get/purchasedetails response's products or fallback)
        let productObj = null
        if (res.data.products && res.data.products[detail.rawmaterial_id]) {
          productObj = res.data.products[detail.rawmaterial_id]
        } else if (allProducts.length > 0) {
          productObj = allProducts.find(p => p.id === detail.rawmaterial_id)
        }
        if (!productObj) {
          productObj = {
            id: detail.rawmaterial_id,
            name: detail.name || "",
            pack_size: "",
            product_unit: { name: "" },
            trade_price: detail.purchase_unit_price,
          }
        }
        const displayText =
          productObj.name +
          " " +
          (productObj.pack_size || "") +
          " " +
          (productObj.raw_material_unit?.name || productObj.rawMaterialUnit?.name || "")
        return {
          detailId: detail.id, // For backend update
          product: productObj,
          productId: detail.rawmaterial_id,
          displayText: displayText.trim(),
          availableQuantity: detail.availableQuantity,
          quantity: detail.quantity,
          sample: detail.sample_qty || 0,
          bonus: detail.bonus_qty || 0,
          tradePrice: detail.purchase_unit_price,
          totalPrice: detail.total_price,
        }
      })
      setProductRows(rows)
    } catch (err) {
      showAlert("Failed to load purchase details.", "error")
    } finally {
      setLoading(false)
    }
  }

  // Add product row (UPDATED to fetch available quantity/trade price from API)
  const handleAddMore = async () => {
    if (!selectedProduct) {
      showAlert("Please select a RawMaterial.", "error")
      return
    }
    if (productRows.some(row => row.product.id === selectedProduct.id)) {
      showAlert("RawMaterial already added", "error")
      return
    }
    try {
      const token = localStorage.getItem("auth_token")
      const res = await axios.get(
        `${BaseURL}/get/avaqedit/purchase/${selectedProduct.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      )
      const availableQuantity = res.data.ava_quantity ?? 0
      const tradePrice = res.data.trade_price ?? (selectedProduct.trade_price || "0.00")
      const displayText =
        selectedProduct.name +
        " " +
        (selectedProduct.pack_size || "") +
        " " +
        (selectedProduct.raw_material_unit?.name || selectedProduct.rawMaterialUnit?.name || "")
      setProductRows([
        ...productRows,
        {
          product: selectedProduct,
          productId: selectedProduct.id,
          displayText: displayText.trim(),
          availableQuantity: availableQuantity,
          quantity: 0,
          sample: 0,
          bonus: 0,
          tradePrice: tradePrice,
          totalPrice: 0,
          detailId: null, // new row, no detailId
        },
      ])
      setSelectedProduct(null)
    } catch (err) {
      showAlert("Failed to fetch available quantity.", "error")
    }
  }

  // Remove product row
  const handleRemoveRow = idx => {
    if (productRows.length <= 1) {
      showAlert("Please keep at least one product.", "error")
      return
    }
    const row = productRows[idx]
    if (row.detailId) {
      setRemovedRows(prev => [...prev, row.productId])
    }
    setProductRows(productRows.filter((_, i) => i !== idx))
  }

  // Change row data (quantity, sample, bonus, etc)
  const handleRowChange = (idx, field, value) => {
    let rows = [...productRows]
    let row = { ...rows[idx] }
    value = value.replace(/[^0-9]/g, "")
    row[field] = value
    if (field === "quantity" || field === "tradePrice") {
      const qty = Number(row.quantity)
      const price = Number(row.tradePrice)
      row.totalPrice = (qty * price).toFixed(2)
    }
    rows[idx] = row
    setProductRows(rows)
  }

  // Handle form field change
  const handleFormFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // Handle Save (Update) -- UPDATED FOR API STRUCTURE
  const handleUpdatePurchase = async () => {

    if (
      !formData.purchaseDate ||
      !formData.receiveDate ||
      !formData.paymentMode ||
      // !formData.deliveryBy ||
      !supplier ||
      productRows.length === 0
    ) {
      showAlert("Please fill all required fields.", "error")
      return
    }
    if (productRows.some(row => Number(row.quantity) <= 0)) {
      showAlert("Please enter quantity for all products.", "error")
      return
    }
    setSaving(true)
    try {
      const token = localStorage.getItem("auth_token")
      // Prepare arrays for API
      const editrawmaterial_id = productRows.map(row => row.productId)
      const editbuying_qty = productRows.map(row => row.quantity)
      const editunit_price = productRows.map(row => row.tradePrice)
      const editsample = productRows.map(row => row.sample)
      const editbonus = productRows.map(row => row.bonus)
      const payload = {
        editpurchase_date: formData.purchaseDate,
        editreceive_date: formData.receiveDate,
        editpayment_mode: formData.paymentMode,
        editdelivery_by: formData.deliveryBy,
        editrawmaterial_id,
        editbuying_qty,
        editunit_price,
        editsample,
        editbonus,
      }
      if (removedRows.length > 0) {
        payload.removed_rows = JSON.stringify(removedRows)
      }

      // Use PUT method and id in url
      const res = await axios.put(`${BaseURL}/update/purchase/${purchaseId}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      if (res.data.success || res.data.message?.includes("success")) {
        // showAlert("purchase updated successfully!", "success")
        if (onUpdateSuccess) onUpdateSuccess("purchase updated successfully!")
        setTimeout(() => {
          onClose()
        }, 800)
      } else {
        showAlert(res.data.message || "Failed to update purchase.", "error")
      }
    } catch (err) {
      showAlert("Failed to update purchase.", "error")
    } finally {
      setSaving(false)
    }
  }

  // Autocomplete product options (exclude already added)
  const productOptions = allProducts.filter(
    prod => !productRows.some(row => row.productId === prod.id)
  )

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={style}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" fontWeight={600}>
              Edit purchase
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid
                container
                spacing={4}
                sx={{
                  flexWrap: {
                    xs: "wrap",
                    md: "nowrap",
                  },
                  "@media (max-width:1265px) and (min-width:1200px)": {
                    flexWrap: "nowrap",
                  },
                }}
              >
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Supplier"
                    value={supplier?.name || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    value={formData.mobile_no}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Address"
                    value={formData.address}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                  />
                  {/* Select Product */}
                  <Autocomplete
                    options={productOptions}
                    getOptionLabel={option => {
                      const name = option.name || ""
                      const packSize = option.pack_size || ""
                      const unit =
                        option.raw_material_unit?.name ||
                        option.raw_material_unit?.name ||
                        ""
                      return `${name} ${packSize} ${unit}`.trim()
                    }}
                    value={selectedProduct}
                    onChange={(_, newValue) => setSelectedProduct(newValue)}
                    loading={loadingProducts}
                    fullWidth
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Select Rawmaterial"
                        sx={{ mb: 2 }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText={loadingProducts ? "Loading products..." : "No products found"}
                  />
                </Grid>
                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Purchase Date"
                    type="date"
                    value={formData.purchaseDate}
                    onChange={e => handleFormFieldChange("purchaseDate", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Receive Date"
                    type="date"
                    value={formData.receiveDate}
                    onChange={e => handleFormFieldChange("receiveDate", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ mb: 1 }}>Payment Mode</Typography>
                    <RadioGroup
                      row
                      value={formData.paymentMode}
                      onChange={e => handleFormFieldChange("paymentMode", e.target.value)}
                    >
                      <FormControlLabel value="cash" control={<Radio />} label="Cash" />
                      <FormControlLabel value="card" control={<Radio />} label="Card" />
                      <FormControlLabel value="check" control={<Radio />} label="Check" />
                    </RadioGroup>
                  </Box>
                  <TextField
                    fullWidth
                    label="Delivery By"
                    value={formData.deliveryBy}
                    onChange={e => handleFormFieldChange("deliveryBy", e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      my: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{
                        bgcolor: "#61676f",
                        color: "white",
                        px: 2,
                        py: 0.5,
                        fontWeight: "bold",
                        fontSize: 17,
                        borderRadius: 1,
                        boxShadow: 2,
                        "&:hover": { bgcolor: "#42474e" },
                      }}
                      onClick={handleAddMore}
                    >
                      ADD MORE
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              {/* Product Row Table */}
              {productRows.length > 0 && (
                <Box
                  sx={{
                    mt: 3,
                    borderRadius: 2,
                    boxShadow: 1,
                    bgcolor: "#f8f8f8",
                    p: 2,
                    overflowX: "auto",
                  }}
                >
                  {productRows.map((row, idx) => (
                    <Box
                      key={row.detailId || row.productId || idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        bgcolor: "#f8f8f8",
                        p: 1,
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: 1,
                        flexWrap: "nowrap",
                        minWidth: 700,
                      }}
                    >
                      {/* Product Name */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          mx: 1,
                          minWidth: 120,
                          maxWidth: 150,
                          flexBasis: 120,
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            width: "100%",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            textAlign: "center",
                          }}
                        >
                          {row.displayText}
                        </Typography>
                      </Box>
                      {/* Available Quantity */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                        <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                          Available Quantity
                        </Typography>
                        <TextField
                          value={row.availableQuantity}
                          InputProps={{
                            readOnly: true,
                            sx: { bgcolor: "#eaeaea", fontWeight: 600, borderRadius: 1 },
                          }}
                          size="small"
                          sx={{ width: 120 }}
                        />
                      </Box>
                      {/* Quantity */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                        <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                          Quantity
                        </Typography>
                        <TextField
                          type="number"
                          value={row.quantity}
                          onChange={e => handleRowChange(idx, "quantity", e.target.value)}
                          size="small"
                          inputProps={{ min: 0 }}
                          sx={{ width: 120 }}
                        />
                      </Box>
                      {/* Sample */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                        <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                          Sample
                        </Typography>
                        <TextField
                          type="number"
                          value={row.sample}
                          onChange={e => handleRowChange(idx, "sample", e.target.value)}
                          size="small"
                          inputProps={{ min: 0 }}
                          sx={{ width: 120 }}
                        />
                      </Box>
                      {/* Bonus */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                        <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                          Bonus
                        </Typography>
                        <TextField
                          type="number"
                          value={row.bonus}
                          onChange={e => handleRowChange(idx, "bonus", e.target.value)}
                          size="small"
                          inputProps={{ min: 0 }}
                          sx={{ width: 120 }}
                        />
                      </Box>
                      {/* Trade Price */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                        <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                          Trade Price
                        </Typography>
                        <TextField
                          value={row.tradePrice}
                          onChange={e => handleRowChange(idx, "tradePrice", e.target.value)}
                          size="small"
                          sx={{ width: 120 }}
                        />
                      </Box>
                      {/* Total Price */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                        <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                          Total Price
                        </Typography>
                        <TextField
                          value={row.totalPrice}
                          InputProps={{
                            readOnly: true,
                            sx: { bgcolor: "#eaeaea", fontWeight: 600, borderRadius: 1 },
                          }}
                          size="small"
                          sx={{ width: 120 }}
                        />
                      </Box>
                      {/* Remove Button */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mx: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ mb: 0.5, fontWeight: 600, color: "transparent" }}
                        >
                          cross
                        </Typography>
                        <IconButton
                          sx={{
                            bgcolor: "#e53935",
                            color: "white",
                            "&:hover": { bgcolor: "#c62828" },
                            borderRadius: 1,
                            width: 28,
                            height: 28,
                          }}
                          onClick={() => handleRemoveRow(idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                  {/* Total Price Box */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <TextField
                      value={totalSum}
                      label="Total Price"
                      InputProps={{
                        readOnly: true,
                        sx: { bgcolor: "#eaeaea", fontWeight: 600, borderRadius: 1 },
                        endAdornment: <InputAdornment position="end">৳</InputAdornment>,
                      }}
                      size="medium"
                      sx={{ width: 120, mr: { xs: 0, md: 2 } }}
                    />
                  </Box>
                </Box>
              )}
              {/* Footer */}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#04b6e0",
                    color: "white",
                    px: 2,
                    py: 0.5,
                    fontSize: 18,
                    fontWeight: 550,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "#0299be" },
                  }}
                  onClick={handleUpdatePurchase}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={24} color="inherit" /> : "Update"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
      {/* Snackbar Alert */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={3000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setAlertOpen(false)}
          severity={alertType}
          elevation={6}
          variant="filled"
          sx={{ fontWeight: 600, fontSize: 16 }}
        >
          {alertMsg}
        </MuiAlert>
      </Snackbar>
    </>
  )
}